import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CountryCode, Money } from '@nokowazzi/types';
import { Badge, Button, IconButton, PriceTag, ImageCarousel, Skeleton, TopAppBar } from '@nokowazzi/ui';
import { QueryErrorState } from '../features/home/components/QueryErrorState';
import { LOCALE_CURRENCY, pickI18nText, toLocale } from '../features/home/format';
import { useProductQuery } from '../features/home/queries';
import { productImageUrl } from '../features/home/urls';
import { convertForViewer, useFxRates } from '../lib/fx';

export const Route = createFileRoute('/products/$productId')({
  component: ProductDetailScreen,
});

const COUNTRY_FLAG: Record<CountryCode, string> = { KR: '🇰🇷', JP: '🇯🇵' };
const COUNTRY_LABEL_KEY: Record<CountryCode, string> = {
  KR: 'auth.countryKr',
  JP: 'auth.countryJp',
};

function ProductDetailScreen() {
  const { productId } = Route.useParams();
  const { t, i18n } = useTranslation();
  const locale = toLocale(i18n.language);
  const viewerCurrency = LOCALE_CURRENCY[locale];
  const router = useRouter();
  const navigate = useNavigate();
  const productQuery = useProductQuery(productId);
  const { data: rates } = useFxRates();

  const goBack = () => {
    if (window.history.length > 1) router.history.back();
    else void navigate({ to: '/' });
  };

  const product = productQuery.data;
  const title = product ? pickI18nText(product.title_i18n, locale) : '';
  const description = product ? pickI18nText(product.description_i18n, locale) : '';
  const price: Money | undefined = product
    ? { amount: product.price_amount, currency: product.currency }
    : undefined;
  const converted = price ? convertForViewer(price, viewerCurrency, rates) : undefined;
  const soldOut = product?.stock === 0;

  return (
    <div className="flex min-h-dvh flex-col pb-[calc(88px+var(--safe-bottom))]">
      <TopAppBar
        leading={
          <IconButton aria-label={t('actions.back')} onClick={goBack}>
            <ArrowLeft />
          </IconButton>
        }
      />

      {productQuery.isPending ? (
        <div className="flex flex-col gap-4 px-5" aria-hidden="true">
          <Skeleton className="aspect-square w-full rounded-lg" />
          <Skeleton variant="text" className="w-4/5" />
          <Skeleton variant="text" className="w-2/5" />
          <Skeleton variant="text" className="w-full" />
          <Skeleton variant="text" className="w-3/5" />
        </div>
      ) : productQuery.isError || product === undefined || price === undefined ? (
        <QueryErrorState
          className="mt-10"
          error={productQuery.error}
          onRetry={() => void productQuery.refetch()}
        />
      ) : (
        <>
          <div className="px-5">
            <ImageCarousel
              images={product.images
                .map((path) => productImageUrl(path))
                .filter((url) => url !== '')}
              altPrefix={title}
            />
          </div>
          <div className="flex flex-col gap-3 px-5 pt-4">
            <div>
              <Badge tone="neutral">
                <span aria-hidden="true">{COUNTRY_FLAG[product.origin_country]}</span>
                {t(COUNTRY_LABEL_KEY[product.origin_country])}
              </Badge>
            </div>
            <h1 className="text-title2 text-fg">{title}</h1>
            <PriceTag
              size="lg"
              emphasis
              money={price}
              converted={converted ? { money: converted } : undefined}
            />
            {description !== '' ? (
              <p className="text-body text-fg-secondary whitespace-pre-wrap">{description}</p>
            ) : null}
            {product.source_url !== null && product.source_url !== '' ? (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  rightIcon={<ExternalLink size={16} />}
                  onClick={() => {
                    window.open(product.source_url ?? '', '_blank', 'noopener,noreferrer');
                  }}
                >
                  {t('product.viewSource')}
                </Button>
              </div>
            ) : null}
          </div>

          <div className="border-line bg-surface fixed inset-x-0 bottom-0 z-40 border-t px-5 pt-3 pb-[calc(var(--safe-bottom)+12px)]">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              disabled={soldOut}
              onClick={() => void navigate({ to: '/sourcing/new' })}
            >
              {soldOut ? t('home.soldOut') : t('product.requestThis')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
